import type { Theme } from "@mui/material/styles";
import type { AppReportChatScreenStyles } from "../types";
import type { ReportChatShellColors } from "../../../../lib/ui/reportChatShell";

/** Shared chrome for the history and new-chat controls. 40px is ~20% above the previous 34px icon button. */
function canvasActionChrome({ c, darkMode }: { c: ReportChatShellColors; darkMode: boolean }) {
  return {
    height: 40,
    minHeight: 40,
    boxSizing: "border-box" as const,
    color: c.assistantText,
    borderRadius: "12px",
    bgcolor: darkMode ? "rgba(30,31,32,0.92)" : c.surface,
    border: `1px solid ${c.border}`,
    boxShadow: darkMode ? "0 2px 8px rgba(0,0,0,0.35)" : "0 1px 4px rgba(32,33,36,0.12)",
    "&:hover": {
      bgcolor: darkMode ? "rgba(45,47,48,0.96)" : c.surface,
      borderColor: c.border,
    },
  };
}

export function buildReportChatScreenStyles(theme: Theme): AppReportChatScreenStyles {
  return {
    root: ({ isPageFullscreen, darkMode, c }) => ({
      position: isPageFullscreen ? "fixed" : "relative",
      ...(isPageFullscreen
        ? {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            maxHeight: "100dvh",
            zIndex: (theme) => theme.zIndex.modal,
            borderRadius: 0,
          }
        : {}),
      display: "flex",
      flexDirection: "row",
      height: "100%",
      bgcolor: c.appBg,
      borderRadius: isPageFullscreen ? 0 : 2,
      overflow: "hidden",
      boxShadow: darkMode
        ? "inset 0 0 0 1px rgba(255,255,255,0.06)"
        : "inset 0 0 0 1px rgba(32,33,36,0.06)",
    }),
    fullscreenToggle: ({ c, darkMode }) => ({
      position: "absolute",
      right: 10,
      top: 10,
      zIndex: 6,
      color: c.muted,
      borderRadius: "12px",
      bgcolor: darkMode ? "rgba(30,31,32,0.92)" : c.surface,
      border: `1px solid ${c.border}`,
      boxShadow: darkMode ? "0 2px 8px rgba(0,0,0,0.35)" : "0 1px 4px rgba(32,33,36,0.12)",
      "&:hover": {
        bgcolor: darkMode ? "rgba(45,47,48,0.96)" : c.surface,
        borderColor: c.border,
      },
    }),
    historyRail: ({ c, historyRailOpen, historyRailExpanded }) => {
      const open = {
        xs: historyRailExpanded ? 300 : 260,
        sm: historyRailExpanded ? 380 : 288,
      };
      const closed = { xs: 0, sm: 0 };
      return {
        flexShrink: 0,
        m: historyRailOpen ? 1.25 : 0,
        borderRadius: "6px",
        border: historyRailOpen ? `1px solid ${c.border}` : "none",
        borderColor: c.border,
        bgcolor: c.surface,
        boxShadow: historyRailOpen
          ? theme.palette.mode === "dark"
            ? "0 1px 2px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.2)"
            : "0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 16px rgba(15, 23, 42, 0.06)"
          : "none",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        pointerEvents: historyRailOpen ? "auto" : "none",
        transition: theme.transitions.create(
          ["width", "min-width", "margin", "box-shadow", "border-color"],
          {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.shorter,
          }
        ),
        width: historyRailOpen ? open : closed,
        minWidth: historyRailOpen ? open : closed,
      };
    },
    historyRailHeader: { px: 1.5, pt: 2, pb: 1.5 },
    newChatButton: (c) => ({
      py: 1.1,
      borderRadius: "12px",
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.875rem",
      color: c.assistantText,
      bgcolor: c.surface,
      border: `1px solid ${c.border}`,
      boxShadow: "0 1px 2px rgba(32,33,36,0.08)",
      "&:hover": { bgcolor: c.surface, borderColor: c.border },
    }),
    historySectionHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mt: 1.5,
      mb: 0.5,
      gap: 0.5,
    },
    historyMutedCaption: (c) => ({ color: c.muted, fontWeight: 600, letterSpacing: 0.4 }),
    historyIconMuted: (c) => ({ color: c.muted }),
    historyFilterBox: {
      mb: 1,
      mt: 0.5,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    historySearchField: (c) => ({
      "& .MuiOutlinedInput-root": {
        borderRadius: 1,
        bgcolor: c.surface,
        fontSize: "0.8125rem",
      },
    }),
    historyDateRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      columnGap: 2,
      rowGap: 2,
      pt: 0.5,
      alignItems: "stretch",
      "& > *": { width: "100%", minWidth: 0 },
    },
    historyDateField: (c) => ({
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 1,
        bgcolor: c.surface,
        fontSize: "0.75rem",
      },
      "& .MuiInputLabel-root": {
        fontSize: "0.75rem",
      },
      "& .MuiInputLabel-shrink": {
        transform: "translate(14px, -8px) scale(0.85)",
      },
    }),
    historyListScroll: { flex: 1, overflow: "auto", px: 0.5, pb: 2 },
    historyLoadingRow: { px: 1.5, py: 2, display: "flex", alignItems: "center", gap: 1 },
    historyLoadingSpinner: (c) => ({ color: c.sparkle }),
    historyLoadingText: (c) => ({ color: c.muted }),
    historyEmptyBox: { px: 1.5, py: 1 },
    historyEmptyText: (c) => ({ color: c.muted, fontSize: "0.8125rem" }),
    historyItem: (c) => ({
      borderRadius: "12px",
      mx: 0.5,
      mb: 0.25,
      py: 1,
      alignItems: "flex-start",
      "&.Mui-selected": { bgcolor: c.selectedList },
      "&.Mui-selected:hover": { bgcolor: c.selectedList },
    }),
    historyItemInner: { minWidth: 0, width: "100%" },
    historyItemTitleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 0.5,
    },
    historyItemTitle: ({ c, historyRailExpanded }) => ({
      fontWeight: 500,
      fontSize: "0.875rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: historyRailExpanded ? "260px" : "170px",
      color: c.assistantText,
    }),
    historyItemDelete: (c) => ({ color: c.muted, p: 0.25 }),
    historyItemUsername: (c) => ({ color: c.muted, fontSize: "0.72rem", display: "block" }),
    historyItemDate: (c) => ({ color: c.muted, fontSize: "0.7rem", display: "block" }),
    mainCanvas: (c) => ({
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      bgcolor: c.appBg,
      position: "relative",
    }),
    canvasActionRow: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      flexShrink: 0,
      px: 1.5,
      pt: 1.5,
      pb: 0.5,
    },
    showHistoryButton: (args) => ({
      ...canvasActionChrome(args),
      width: 40,
      minWidth: 40,
      p: 0,
    }),
    canvasIconButton: (args) => ({
      ...canvasActionChrome(args),
      width: 40,
      minWidth: 40,
      p: 0,
    }),
    historyRunningIcon: {
      display: "inline-flex",
      alignItems: "center",
      flexShrink: 0,
    },
    threadScroll: {
      flex: 1,
      overflow: "auto",
      display: "flex",
      flexDirection: "column",
    },
    threadInner: (_historyRailOpen) => ({
      flex: 1,
      width: "100%",
      maxWidth: 872,
      mx: "auto",
      px: { xs: 2, sm: 3 },
      py: { xs: 2, sm: 3 },
      display: "flex",
      flexDirection: "column",
      gap: 2.5,
    }),
    conversationLoading: {
      flex: 1,
      minHeight: 240,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    conversationLoadingSpinner: (c) => ({ color: c.sparkle }),
    emptyState: {
      flex: 1,
      minHeight: 200,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      px: 2,
      pt: { xs: 2, sm: 6 },
    },
    emptyStateIcon: (_darkMode) => ({
      width: 56,
      height: 56,
      objectFit: "contain",
      mb: 2,
      borderRadius: 2,
      opacity: 1,
    }),
    emptyStateGreeting: (c) => ({
      fontWeight: 400,
      fontSize: { xs: "1.75rem", sm: "2.25rem" },
      lineHeight: 1.25,
      maxWidth: 560,
      background: c.accentGrad,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
    }),
    emptyStateSubtitle: (c) => ({ color: c.muted, mt: 1.5, maxWidth: 480, fontSize: "0.95rem" }),
    messageRow: (isUser) => ({
      display: "flex",
      justifyContent: isUser ? "flex-end" : "flex-start",
      width: "100%",
    }),
    messageBubbleWrap: (isWideResponse) => ({
      maxWidth: isWideResponse ? "100%" : "min(85%, 640px)",
      width: isWideResponse ? "100%" : "auto",
    }),
    userMessageColumn: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 0.5,
      width: "100%",
    },
    userMetaCaption: (c) => ({
      color: c.muted,
      fontSize: "0.72rem",
      letterSpacing: 0.2,
      textAlign: "right",
      px: 0.25,
    }),
    userScopeRow: {
      display: "flex",
      alignSelf: "center",
      flexShrink: 0,
      maxWidth: "40%",
      px: 0.25,
    },
    userMessageRow: {
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 1.25,
      justifyContent: "flex-end",
      maxWidth: "100%",
    },
    userBubble: (c) => ({
      px: 2,
      py: 1.25,
      borderRadius: "24px 24px 4px 24px",
      bgcolor: c.userBubble,
      color: c.userText,
      border: "none",
      maxWidth: "min(100%, 560px)",
    }),
    userBubbleText: {
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      fontSize: "0.9375rem",
      lineHeight: 1.6,
    },
    userAvatarCircle: (darkMode) => ({
      width: 32,
      height: 32,
      borderRadius: "50%",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
    }),
    userAvatarImage: (darkMode) => ({
      width: 18,
      height: 18,
      objectFit: "contain",
      display: "block",
      opacity: darkMode ? 0.92 : 0.75,
      filter: darkMode ? "invert(1)" : "none",
    }),
    assistantRow: { display: "flex", gap: 1.5, alignItems: "flex-start" },
    assistantAvatarCircle: (_darkMode) => ({
      width: 32,
      height: 32,
      borderRadius: "10px",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      bgcolor: "transparent",
    }),
    assistantAvatarImage: { width: 32, height: 32, objectFit: "cover", display: "block" },
    assistantContent: { flex: 1, minWidth: 0 },
    assistantHeader: { display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 },
    assistantLabel: (c) => ({ color: c.muted, fontWeight: 600, letterSpacing: 0.3 }),
    assistantRail: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 0.75,
      flexShrink: 0,
    },
    assistantActions: { display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 },
    assistantActionButton: (c) => ({ color: c.muted }),
    assistantMetaCaption: (c) => ({
      color: c.muted,
      mb: 0.75,
      fontSize: "0.72rem",
      letterSpacing: 0.2,
    }),
    assistantErrorCaption: {
      color: "error.main",
      mb: 0.75,
      fontSize: "0.72rem",
      fontWeight: 600,
    },
    assistantErrorText: { color: "#ea4335" },
    assistantPayload: (c) => ({ color: c.assistantText, "& *": { maxWidth: "100%" } }),
    assistantThinkingRow: { display: "flex", alignItems: "center", gap: 1, py: 0.5 },
    /** 2×2 mini squares that rotate as a block — chat “thinking” indicator. */
    assistantThinkingBlocks: (_c) => ({
      width: 14,
      height: 14,
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridTemplateRows: "1fr 1fr",
      gap: "2px",
      flexShrink: 0,
      animation: "chatThinkingBlocksSpin 1.15s linear infinite",
      "@keyframes chatThinkingBlocksSpin": {
        to: { transform: "rotate(360deg)" },
      },
    }),
    assistantThinkingBlock: (c) => ({
      borderRadius: "1.5px",
      bgcolor: c.sparkle,
      minWidth: 0,
      minHeight: 0,
      "&:nth-of-type(1)": { opacity: 1 },
      "&:nth-of-type(2)": { opacity: 0.72 },
      "&:nth-of-type(3)": { opacity: 0.48 },
      "&:nth-of-type(4)": { opacity: 0.28 },
    }),
    assistantThinkingText: (c) => ({ color: c.muted }),
    composerFooter: (c) => ({
      flexShrink: 0,
      px: { xs: 2, sm: 3 },
      pb: 2.5,
      pt: 0,
      bgcolor: c.appBg,
    }),
    composerInner: { maxWidth: 872, mx: "auto" },
    composerIndexRow: { mb: 1, alignItems: "center" },
    composerIndexLabel: { mr: 0.5 },
    composerPaper: ({ c, darkMode }) => ({
      display: "flex",
      // Phones: the text field gets its own row, the tool buttons sit beneath it.
      flexWrap: { xs: "wrap", sm: "nowrap" },
      alignItems: "center",
      gap: 0.75,
      p: "10px 12px",
      borderRadius: "16px",
      bgcolor: c.surface,
      border: `1px solid ${c.border}`,
      boxShadow: darkMode
        ? "0 1px 2px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.2)"
        : "0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 16px rgba(15, 23, 42, 0.06)",
    }),
    composerInputPlaceholder: (c) => ({
      order: { xs: -1, sm: 0 },
      flex: { xs: "1 1 100%", sm: "1 1 auto" },
      "& .MuiInputBase-input::placeholder": {
        color: c.muted,
        opacity: 1,
      },
    }),
    composerMicWrapper: { display: "inline-flex" },
    composerMicButton: ({ isListening, c }) => ({
      color: isListening ? "#fff" : c.muted,
      bgcolor: isListening ? "#ea4335" : undefined,
      width: { xs: 44, sm: 36 },
      height: { xs: 44, sm: 36 },
      flexShrink: 0,
      ...(isListening
        ? {
            "&:hover": { bgcolor: "#d93025" },
          }
        : {}),
    }),
    composerSuggestButton: ({ c, busy }) => ({
      color: c.muted,
      width: { xs: 44, sm: 36 },
      height: { xs: 44, sm: 36 },
      flexShrink: 0,
      opacity: busy ? 0.85 : 1,
    }),
    composerSlashHint: (c) => ({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 28,
      height: 28,
      flexShrink: 0,
      borderRadius: "10px",
      bgcolor: "background.tonal",
      color: c.muted,
      fontWeight: 700,
      fontSize: "0.85rem",
      cursor: "default",
      userSelect: "none",
    }),
    composerSendButton: ({ c }) => ({
      width: { xs: 48, sm: 36 },
      height: { xs: 44, sm: 36 },
      ml: { xs: "auto", sm: 0 },
      flexShrink: 0,
      borderRadius: "10px",
      background: c.accentGrad,
      color: c.sendIcon,
      "&:hover": { opacity: 0.92, background: c.accentGrad },
      "&.Mui-disabled": {
        opacity: 0.35,
        background: c.border,
        color: c.muted,
      },
    }),
    composerDisclaimer: (c) => ({
      display: "block",
      textAlign: "center",
      color: c.muted,
      mt: 1,
    }),
    fullscreenDialogPaper: (c) => ({ bgcolor: c.appBg }),
    fullscreenDialogClose: (c) => ({ color: c.muted }),
    fullscreenDialogContent: { p: 2, gap: 0 },
    iconSize20: { fontSize: 20 },
    iconSize22: { fontSize: 22 },
    iconSize26: { fontSize: 26 },
    composerInputProps: (c) => ({
      fontSize: "0.9375rem",
      lineHeight: 1.5,
      color: c.assistantText,
      py: 0.75,
      px: 0.5,
    }),
    printDocumentCss: `
            body { font-family: Arial, sans-serif; padding: 20px; color: #222; }
            pre { white-space: pre-wrap; word-break: break-word; font-family: Menlo, Consolas, monospace; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; }
            th { background: #f5f5f5; }
          `,
  };
}
